import { m } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
// hooks
import { useMockedUser } from 'src/hooks/use-mocked-user';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { LoggedInUser } from 'src/types/user';
import {useLocales} from "../../locales";

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'DASHBOARD',
    linkTo: '/',
  },
  {
    label: 'ACCOUNT',
    linkTo: paths.dashboard.user.account,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();

  const mockedUser = useMockedUser();
  const loggedInUser = mockedUser.user as LoggedInUser;

  // Derive tenant logo URL (prefer client app host when available, else dashboard static)
  const tenantSlug = String(loggedInUser?.tenant || 'nattygains').toLowerCase();
  const headerHost = (() => {
    const raw = (loggedInUser as any)?.url || '';
    if (!raw) return '';
    try {
      const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      return new URL(withProto).origin;
    } catch {
      return '';
    }
  })();
  const tenantLogoSrc = headerHost
    ? `${headerHost}/public/img/logo/${tenantSlug}.png`
    : `/static/img/logo/${tenantSlug}.png`;

  const { logout } = useAuthContext();

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace('/');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClickItem = (path: string) => {
    console.log(path);
    popover.onClose();
    router.push(path);
  };

    const { t } = useLocales();


    return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.32)}`,
          '&:hover': {
            borderColor: 'primary.main',
          },
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            borderColor: (theme) => theme.palette.primary.main,
          }),
        }}
      >
        <Avatar
          src={tenantLogoSrc}
          alt={loggedInUser?.tenant || loggedInUser?.name}
          imgProps={{
            onError: (e: any) => {
              (e.currentTarget as HTMLImageElement).src = '/logo/logo_single.png';
            },
            referrerPolicy: 'no-referrer',
          }}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.primary.main}`,
            bgcolor: 'background.default',
          }}
        />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {loggedInUser?.name}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {loggedInUser?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </CustomPopover>
    </>
  );
}
